// ----------------------------------------------------------------
// <copyright company='Microsoft Corporation'>
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
// ----------------------------------------------------------------

import { call, put, select } from 'redux-saga/effects';

import ClaimOrderService from './../../../dal/ServiceRequestDal';
import {
    TransformCheckoutPayloadProps,
    transformCheckoutPayload,
    transformCheckoutReasonForNotSelectingPayload,
    transformCheckoutOnSiteRepairPayload,
    transformCheckoutSoftwareRepairPayload,
} from './helpers';
import { logException } from './../../../lib/ApplicationInsights';
import CorrelationVector from './../../../lib/CorrelationVector';
import { parsePayload } from './../../../businessLogic/sagas/serviceRequest';

import {
    CHECKOUT_ORDER,
    checkoutOrderSuccess,
    PURCHASE_ORDER,
    Progress,
    purchaseOrderFailed,
    updateCreateOrderProgress,
    ClaimOrderLineItemAttributes,
    PURCHASE_ON_DEMAND_ORDER,
    purchaseOnDemandOrderSuccess,
    SAVE_TRAVEL_SR_OUTCOME
} from './../../../store/orderCreation';
import {
    actionStatusFailed,
    actionStatusStarted,
    actionStatusSuccess,
} from './../../../store/actionStatus';

import { isShippingAddressRequired } from './../../../helpers/shippingChecks';

import { deviceSelector } from './../../../store/deviceInfo/selectors';
import {
    orderCreationSelector,
    selectedShippingOfferSelector,
    deviceShippingOfferSelector,
    shippingAddressSelector,
    shippingOfferSelector,
    allSelectedOfferSelector,
    billingAddressSelector,
    customerServiceLocationAddressSelector,
    isOnSiteRepairStatusSelector,
    customerCommentsSelector,
    isSoftwareOrderSelector,
    softwareRepairDetailsSelector,
} from './../../../store/orderCreation/selectors';
import { userInfoSelector } from './../../../store/user/selector';
import { getServiceRequestDataSuccess } from './../../../store/deviceInfo/claimOrder';
import { onSiteSoldToShipToSelector } from './../../../store/managePartners';

export function* checkoutOrderSaga(): IterableIterator<any> {
    let data: TransformCheckoutPayloadProps | undefined = undefined;
    let payload: any;
    let agentInfo: any;
    let billToEmail: any;
    let device: any;
    let deviceShippingOffers: any;
    let selectedOffers: any;
    let selectedShippingOffers: any;
    let shippingAddress: any;
    let billingAddress: any;
    let shippingOffers: any;
    let orderInfo: any;
    let customerServiceLocationAddress: any;
    let isOnSiteRepair: any;
    let isSoftwareRepair:any;
    let customerComments: any;
    let onSiteSoldToShipTo: any;
    let softwareRepairDetails: any;

    try {
        agentInfo = yield select(state => userInfoSelector(state));

        device = yield select(state => deviceSelector(state));
        deviceShippingOffers = yield select(state =>
            deviceShippingOfferSelector(state)
        );

        orderInfo = yield select(state => orderCreationSelector(state));

        selectedShippingOffers = yield select(state =>
            selectedShippingOfferSelector(state)
        );
        selectedOffers = yield select(state => allSelectedOfferSelector(state));

        shippingAddress = yield select(state =>
            shippingAddressSelector(state)
        );

        softwareRepairDetails = yield select(state => softwareRepairDetailsSelector(state));

        customerServiceLocationAddress = yield select(state => customerServiceLocationAddressSelector(state));
        isOnSiteRepair = yield select(state => isOnSiteRepairStatusSelector(state));
        isSoftwareRepair = yield select(state => isSoftwareOrderSelector(state));
        customerComments = yield select(state => customerCommentsSelector(state));
        onSiteSoldToShipTo = yield select(state => onSiteSoldToShipToSelector(state));

        billingAddress = yield select(state => billingAddressSelector(state));

        shippingOffers = yield select(state => shippingOfferSelector(state));
        billToEmail = shippingAddress.email;

        if (shippingOffers && selectedOffers) {
            if (!isShippingAddressRequired(shippingOffers, selectedOffers)) {
                if (orderInfo && orderInfo.customerInfo) {
                    shippingAddress.firstName = orderInfo.customerInfo.firstName;
                    shippingAddress.lastName = orderInfo.customerInfo.lastName;
                    shippingAddress.email = orderInfo.customerInfo.microsoftAccount;
                    shippingAddress.addressLine1 = agentInfo.addressLineOneText;
                } else {
                    throw new Error('Unexpected error - Registration Info missing.');
                }
            }
            else {
                const overwriteBillingEmail = Array.isArray(selectedOffers) && selectedOffers.some((selectedOffer: any) =>
                    selectedOffer.offerAttributes === ClaimOrderLineItemAttributes.InStore
                );
                if (overwriteBillingEmail) {
                    billToEmail = orderInfo && orderInfo.customerInfo && orderInfo.customerInfo.microsoftAccount;
                }
            }
        }

        if (
            !(
                selectedShippingOffers &&
                Object.keys(selectedShippingOffers).length
            ) &&
            !(
                deviceShippingOffers &&
                Object.keys(deviceShippingOffers).length
            )
        ) {
            yield put(
                actionStatusFailed({
                    actionName: CHECKOUT_ORDER,
                    data: {
                        message: 'selected_components_missing_shipping_offer',
                    },
                    error: new Error('Internal Error: "selectedShippingOffers" was empty'),
                })
            );
            return;
        }

        data = {
            agentInfo,
            billToEmail,
            billingAddress,
            device,
            deviceShippingOffers,
            selectedShippingOffers,
            orderInfo,
            shippingAddress,
            customerServiceLocationAddress,
            customerComments,
            onSiteSoldToShipTo,
            softwareRepairDetails,
        };

        if(isOnSiteRepair){
            payload = transformCheckoutOnSiteRepairPayload(data);
        }
        else if(isSoftwareRepair){
            payload = transformCheckoutSoftwareRepairPayload(data);
        }
        else{
            payload = transformCheckoutPayload(data);
        }

        
    } catch (e) {
        logException(CorrelationVector.getCurrentValue(), e, {
            transformCheckoutPayload: {
                billToEmail,
                device: JSON.stringify(device || {}).substring(0, 512),
                orderInfo: JSON.stringify(orderInfo || {}).substring(0, 512),
                agentInfo: JSON.stringify(agentInfo || {}).substring(0, 512),
                selectedOffers: JSON.stringify(selectedOffers || {}).substring(0, 512),
                selectedShippingOffers: JSON.stringify(selectedShippingOffers || {}).substring(0, 512),
                shippingAddress: JSON.stringify(shippingAddress || {}).substring(0, 512),
                shippingOffers: JSON.stringify(shippingOffers || {}).substring(0, 512),
                deviceShippingOffers: JSON.stringify(deviceShippingOffers || {}).substring(0, 512),
            },
        });
        yield put(
            actionStatusFailed({
                actionName: CHECKOUT_ORDER,
                data: {
                    ...data,
                },
                error: e,
            })
        );
        return;
    }

    try {
        yield put(actionStatusStarted({ actionName: CHECKOUT_ORDER }));
        const checkedOutOrder = yield call(ClaimOrderService.checkout,payload);

        yield put(
            checkoutOrderSuccess({
                data: checkedOutOrder && parsePayload(checkedOutOrder),
            })
        );
        yield put(actionStatusSuccess({ actionName: CHECKOUT_ORDER }));
        yield put(updateCreateOrderProgress(Progress.PlacingOrder));
    } catch (e) {
        yield put(
            actionStatusFailed({
                actionName: CHECKOUT_ORDER,
                data: {
                    ...payload,
                },
                error: e,
            })
        );
    }
}

export function* purchaseOrder() {
    let orderId: string = '';

    try {
        orderId = yield select(
            (state: any): string => state.order && state.order.confirmation && state.order.confirmation.claimOrderId as string
        ) || '';

        yield put(actionStatusStarted({ actionName: PURCHASE_ORDER }));

        const purchasedOrder: any = yield call(ClaimOrderService.purchase, orderId);

        const parsedPayload = purchasedOrder && parsePayload(purchasedOrder);
        if (!parsedPayload || !parsedPayload.claimOrderId) {
            throw new Error('finalizing_order_failed');
        }

        yield put(getServiceRequestDataSuccess(parsedPayload));
        yield put(actionStatusSuccess({ actionName: PURCHASE_ORDER }));
    } catch (e) {
        yield put(
            actionStatusFailed({
                actionName: PURCHASE_ORDER,
                data: {
                    claimOrderId: orderId,
                },
                error: e,
            })
        );
        yield put(
            purchaseOrderFailed({
                claimOrderId: orderId,
            })
        );
    }
}

export function* travelOutcome(request: any) {
    const {claimOrderId, action, travelSrOutcome} = request.payload;

    try {
        yield put(actionStatusStarted({ actionName: `${SAVE_TRAVEL_SR_OUTCOME}-${claimOrderId}` }));

        const travelOutcomeResponse: any = yield call(ClaimOrderService.travelSROutcome, claimOrderId, action, travelSrOutcome);

        const parsedPayload = travelOutcomeResponse && parsePayload(travelOutcomeResponse);
        if (!parsedPayload || !parsedPayload.claimOrderId) {
            throw new Error('finalizing_order_failed');
        }

        yield put(getServiceRequestDataSuccess(parsedPayload));
        yield put(actionStatusSuccess({ actionName: `${SAVE_TRAVEL_SR_OUTCOME}-${claimOrderId}` }));
    } catch (e) {
        yield put(
            actionStatusFailed({
                actionName: `${SAVE_TRAVEL_SR_OUTCOME}-${claimOrderId}`,
                data: {
                    claimOrderId,
                },
                error: e,
            })
        );
    }
}

export function* purchaseOnDemandOrder(request:any): IterableIterator<any> {
    let data: TransformCheckoutPayloadProps | undefined = undefined;
    let payload: any;
    let agentInfo: any;
    let billToEmail: any;
    let device: any;
    let deviceShippingOffers: any;
    let selectedOffers: any;
    let selectedShippingOffers: any;
    let shippingAddress: any;
    let billingAddress: any;
    let shippingOffers: any;
    let orderInfo: any;
    const {isOnDemandCheckOut, isOnSiteCheckOut, claimOrderType, secondSRCreation} = request.payload;
    let customerServiceLocationAddress: any;
    let customerComments: any;
    const onSiteSoldToShipTo: any = null;
    const softwareRepairDetails: any = null;

    try {
        agentInfo = yield select(state => userInfoSelector(state));

        device = yield select(state => deviceSelector(state));
        deviceShippingOffers = yield select(state =>
            deviceShippingOfferSelector(state)
        );

        orderInfo = yield select(state => orderCreationSelector(state));

        selectedShippingOffers = yield select(state =>
            selectedShippingOfferSelector(state)
        );
        selectedOffers = yield select(state => allSelectedOfferSelector(state));

        shippingAddress = yield select(state =>
            shippingAddressSelector(state)
        );

        customerServiceLocationAddress = null;
        customerComments = null;

        billingAddress = yield select(state => billingAddressSelector(state));

        shippingOffers = yield select(state => shippingOfferSelector(state));
        billToEmail = shippingAddress.email;

        if (shippingOffers && selectedOffers) {
            if (!isShippingAddressRequired(shippingOffers, selectedOffers)) {
                if(orderInfo && orderInfo.reasonForNotSelectingOffers.repairComments === ''){
                    if (orderInfo && orderInfo.customerInfo) {
                        shippingAddress.firstName = orderInfo.customerInfo.firstName;
                        shippingAddress.lastName = orderInfo.customerInfo.lastName;
                        shippingAddress.email = orderInfo.customerInfo.microsoftAccount;
                        shippingAddress.addressLine1 = agentInfo.addressLineOneText;
                    } else {
                        throw new Error('Unexpected error - Registration Info missing.');
                    }
                }
            }
            else {
                const overwriteBillingEmail = Array.isArray(selectedOffers) && selectedOffers.some((selectedOffer: any) =>
                    selectedOffer.offerAttributes === ClaimOrderLineItemAttributes.InStore
                );
                if (overwriteBillingEmail) {
                    billToEmail = orderInfo && orderInfo.customerInfo && orderInfo.customerInfo.microsoftAccount;
                }
            }
        }
        if(orderInfo.reasonForNotSelectingOffers.repairComments === ''){
            if (
                !(
                    selectedShippingOffers &&
                    Object.keys(selectedShippingOffers).length
                ) &&
                !(
                    deviceShippingOffers &&
                    Object.keys(deviceShippingOffers).length
                )
            ) {
                yield put(
                    actionStatusFailed({
                        actionName: PURCHASE_ON_DEMAND_ORDER,
                        data: {
                            message: 'selected_components_missing_shipping_offer',
                        },
                        error: new Error('Internal Error: "selectedShippingOffers" was empty'),
                    })
                );
                return;
            }
        }

        data = {
            agentInfo,
            billToEmail,
            billingAddress,
            device,
            deviceShippingOffers,
            selectedShippingOffers,
            shippingAddress,
            orderInfo,
            customerServiceLocationAddress,
            customerComments,
            onSiteSoldToShipTo,
            softwareRepairDetails,
        };

        if(typeof orderInfo.reasonForNotSelectingOffers.repairComments !== 'undefined' && orderInfo.reasonForNotSelectingOffers.repairComments !== ''){
            payload = transformCheckoutReasonForNotSelectingPayload(data);
        } else {
            payload = transformCheckoutPayload(data);
            payload.ClaimOrderId = device.claims[0].claimOrderId;
        }
        payload.IsOnDemandCheckOut = isOnDemandCheckOut;
        
        if(secondSRCreation){
            payload.IsOnsiteCheckOut = isOnSiteCheckOut;
            payload.ClaimOrderType = claimOrderType;
        }

    } catch (e) {
        logException(CorrelationVector.getCurrentValue(), e, {
            transformCheckoutPayload: {
                billToEmail,
                device: JSON.stringify(device || {}).substring(0, 512),
                orderInfo: JSON.stringify(orderInfo || {}).substring(0, 512),
                agentInfo: JSON.stringify(agentInfo || {}).substring(0, 512),
                selectedOffers: JSON.stringify(selectedOffers || {}).substring(0, 512),
                selectedShippingOffers: JSON.stringify(selectedShippingOffers || {}).substring(0, 512),
                shippingAddress: JSON.stringify(shippingAddress || {}).substring(0, 512),
                shippingOffers: JSON.stringify(shippingOffers || {}).substring(0, 512),
                deviceShippingOffers: JSON.stringify(deviceShippingOffers || {}).substring(0, 512),
            },
        });
        yield put(
            actionStatusFailed({
                actionName: PURCHASE_ON_DEMAND_ORDER,
                data: {
                    ...data,
                },
                error: e,
            })
        );
        return;
    }

    try {
        yield put(actionStatusStarted({ actionName: PURCHASE_ON_DEMAND_ORDER }));
        const checkedOutOrder = yield call(ClaimOrderService.onDemandPurchase,payload);
        const parsedPayload = checkedOutOrder && parsePayload(checkedOutOrder);
        if(!isOnSiteCheckOut) {
            yield put(getServiceRequestDataSuccess(parsedPayload));
        }
        yield put(
            purchaseOnDemandOrderSuccess({
                data: checkedOutOrder && parsePayload(checkedOutOrder),
            })
        );
        yield put(actionStatusSuccess({ actionName: PURCHASE_ON_DEMAND_ORDER }));
        if(isOnDemandCheckOut || isOnSiteCheckOut) {
            yield put(updateCreateOrderProgress(Progress.PlacingOrder));
        }
    } catch (e) {
        yield put(
            actionStatusFailed({
                actionName: PURCHASE_ON_DEMAND_ORDER,
                data: {
                    ...payload,
                },
                error: e,
            })
        );
    }
}